import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import {
  compareSpreadsheetColumnIds as areSpreadsheetColumnIdsEqual, isSpreadsheetColumnId,
} from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { copy } from '~/_shared/utils/collections/collections';
import { createUuid } from '~/_shared/utils/createUuid';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import type { MapSettingsChartsAction } from './mapSettingsCharts.action';
import {
  mapSettingsChartsAddOrUpdateChart, mapSettingsChartsRemoveCharts,
} from './mapSettingsCharts.actionCreators';
import type { MapSettingsChartsState } from './mapSettingsCharts.state';
import {
  type Chart, type ChartWithRefMetrics, isChartMetricLocationCount, isChartMetricSpreadsheetColumn,
} from './mapSettingsCharts.types';

export const mapSettingsChartsInitialState: MapSettingsChartsState = {
  columnsMap: {},
  items: [],
};

export const mapSettingsChartsReducer = (
  state = mapSettingsChartsInitialState,
  action: MapSettingsChartsAction
): MapSettingsChartsState => {

  if (mapSettingsChartsAddOrUpdateChart.match(action)) {
    const { columnsMap, chart } = updateColumnsMapAndReplaceColumnIdsWithRefs(
      state.columnsMap,
      action.payload.chart
    );

    const existingIndex = state.items.findIndex(chart => chart.id === action.payload.chart.id);
    if (existingIndex === -1) {
      return {
        ...state,
        columnsMap,
        items: [...state.items, chart],
      };
    }

    return {
      ...state,
      columnsMap,
      items: copy.andReplace(state.items, existingIndex, chart),
    };
  }

  if (mapSettingsChartsRemoveCharts.match(action)) {
    const newItems = state.items.filter(chart => !action.payload.ids.has(chart.id));

    return {
      ...state,
      items: newItems,
    };
  }

  return state;
};

const updateColumnsMapAndReplaceColumnIdsWithRefs = (
  columnsMap: MapSettingsChartsState['columnsMap'],
  chart: Chart,
): {
  columnsMap: MapSettingsChartsState['columnsMap'];
  chart: ChartWithRefMetrics;
} => {
  const replacedMetrics = chart.metrics.reduce((accum, metric) => {

    const getColumnRefIdAndUpdateColumnsMap = (
      columnsMapState: MapSettingsChartsState['columnsMap'],
      { spreadsheetId, columnId }: { spreadsheetId: SpreadsheetId; columnId?: ColumnId },
    ) => {
      const refIdFromExisting = stringObjectEntries(columnsMapState)
        .find(([_, spreadsheetWithMaybeColumnId]) => ((
          columnId && isSpreadsheetColumnId(spreadsheetWithMaybeColumnId)
            && areSpreadsheetColumnIdsEqual(spreadsheetWithMaybeColumnId, { spreadsheetId, columnId })
        ) || (!columnId && spreadsheetWithMaybeColumnId.spreadsheetId === spreadsheetId)
        ))?.[0];

      let finalRefId = refIdFromExisting;
      if (!finalRefId) {
        finalRefId = createUuid();
        accum.columnsMap = {
          ...accum.columnsMap,
          [finalRefId]: {
            spreadsheetId,
            ...(columnId ? { columnId } : {}),
          },
        };
      }

      return {
        refId: finalRefId,
        updatedColumnsMap: accum.columnsMap,
      };
    };

    if (isChartMetricSpreadsheetColumn(metric)) {
      const { refId, updatedColumnsMap } = getColumnRefIdAndUpdateColumnsMap(
        accum.columnsMap,
        metric.column
      );

      return {
        ...accum,
        columnsMap: updatedColumnsMap,
        metrics: [
          ...accum.metrics, {
            ...metric,
            column: refId,
          } satisfies ChartWithRefMetrics['metrics'][number],
        ],
      };
    }
    else if (isChartMetricLocationCount(metric)) {
      const metricColumn = metric.column;

      // For group columns, store the whole column in columnsMap
      if (metricColumn) {
        const { refId, updatedColumnsMap } = getColumnRefIdAndUpdateColumnsMap(
          accum.columnsMap,
          metric.column,
        );

        return {
          ...accum,
          columnsMap: updatedColumnsMap,
          metrics: [
            ...accum.metrics, {
              ...metric,
              column: refId,
              spreadsheetId: undefined,
            } satisfies ChartWithRefMetrics['metrics'][number],
          ],
        };
      }
      // For total location count (no column), get ref just for spreadsheetId
      else if (metric.spreadsheetId) {
        const { refId, updatedColumnsMap } = getColumnRefIdAndUpdateColumnsMap(
          accum.columnsMap,
          { spreadsheetId: metric.spreadsheetId },
        );

        return {
          ...accum,
          columnsMap: updatedColumnsMap,
          metrics: [
            ...accum.metrics, {
              ...metric,
              column: undefined,
              spreadsheetId: refId,
            } satisfies ChartWithRefMetrics['metrics'][number],
          ],
        };
      }
    }
    else {
      accum.metrics = [
        ...accum.metrics,
        metric,
      ];
    }
    return accum;
  }, {
    columnsMap,
    metrics: [] as ChartWithRefMetrics['metrics'],
  });

  return {
    columnsMap: replacedMetrics.columnsMap,
    chart: {
      ...chart,
      metrics: replacedMetrics.metrics,
    } satisfies ChartWithRefMetrics,
  };
};
